import { GetStaticProps } from 'next'
import { useState } from 'react'
import { getClient } from '../../lib/sanity.client'
import { blogListQuery, blogCategoriesQuery, getNavigation } from '../../lib/sanity.queries'
import { urlForImage } from '../../lib/sanity.image'
import Link from 'next/link'
import Image from 'next/image'
import { format } from 'date-fns'
import { readToken } from '~/lib/sanity.api'
import { Column, Grid } from '~/components/Grid'
import { motion } from 'framer-motion'
import Eyebrow from '~/components/Eyebrow'
import Card from '~/components/Card'
import Layout from '~/components/Layout'
import Head from 'next/head'
import { PAGE_TITLE_PREFIX } from '~/components/constants'


interface BlogsPageProps {
  posts: any[]
  categories: any[]
  navigation: any 
}

export const getStaticProps: GetStaticProps = async ({ draftMode = false }) => {
  const client = getClient(draftMode ? { token: readToken } : undefined)
  const posts = await client.fetch(blogListQuery)
  const categories = await client.fetch(blogCategoriesQuery)
  const navigation = await getNavigation(client)

  return {
    props: {
      posts,
      categories,
      draftMode,
      token: draftMode ? readToken : '',
      navigation,
    },
    revalidate: 60,
  }
}

export default function BlogsPage({ posts, categories, navigation }: BlogsPageProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState<string>('')
  
  const filteredPosts = posts
    .filter(post => selectedCategory === 'all' || post.categorySlug === selectedCategory)
    .filter(post => {
      const searchLower = searchQuery.toLowerCase()
      return (
        post.title.toLowerCase().includes(searchLower) ||
        post.excerpt?.toLowerCase().includes(searchLower) ||
        post.category?.toLowerCase().includes(searchLower)
      )
    })

  return (
    <>
    <Head>
      <title>{`${PAGE_TITLE_PREFIX} Blog`}</title>
    </Head>
    <Layout navigation={navigation}>
    <div className="pt-header md:pt-header-lg">
      <Grid>
        <Column className="col-span-12 md:col-span-10 md:col-start-2">
          <div className="flex flex-col items-start justify-center z-10 w-full mt-4 md:mt-20">
            <div className="text-left xl:text-center w-full">
                <h1 className="h1 text-gradient m-auto text-pretty mb-4 xl:mb-20">
                  Our Blog
                </h1>
            </div>
          </div>
        </Column>
      </Grid>

      {/* Search Bar */}
      <Grid className="mb-8">
        <Column className="col-span-12">
          <input
            type="text"
            placeholder="Search posts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-6 py-3 rounded-full bg-grey-400 text-white placeholder-grey border border-grey-400 focus:border-green focus:outline-none transition-colors"
          />
        </Column>
      </Grid>

      {/* Categories Filter */}
      <Grid className="mb-20">
        <Column className="col-span-12">
          <div className="flex flex-row items-center justify-start w-full flex-grow gap-4 flex-wrap">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-6 py-3 rounded-full transition-all duration-300 ${
                selectedCategory === 'all'
                  ? 'bg-green text-black'
                  : 'bg-grey-400 hover:bg-off-black text-white'
              }`}
            >
              All
            </button>
            {categories.map((category) => (
              <button
                key={category._id}
                onClick={() => setSelectedCategory(category.slug.current)}
                className={`px-6 py-3 rounded-full transition-all duration-300 ${
                  selectedCategory === category.slug.current
                    ? 'bg-green text-black'
                    : 'bg-grey-400 hover:bg-off-black text-white'
                }`}
              >
                {category.title}
              </button>
            ))}
          </div>
        </Column>
      </Grid>

      {/* Blog Posts Grid */}
      <Grid>
        {filteredPosts.map((post, index) => (
          <Column key={post._id} className="col-span-12 md:col-span-6 lg:col-span-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Link href={`/blog/${post.slug.current}`}>
                <Card className="h-full hover:scale-[1.02] transition-transform duration-300">
                  {post.mainImage && (
                    <div className="relative h-48 mb-6 rounded-lg overflow-hidden">
                      <Image
                        src={urlForImage(post.mainImage).url()}
                        alt={post.title}
                        fill
                        className="object-cover rounded-xl"
                      />
                    </div>
                  )}
                  <Eyebrow className="max-w-fit">{post.category}</Eyebrow>
                  <h2 className="h5 text-lg text-gradient my-4">{post.title}</h2>
                  <p className="text-grey mb-6">{post.excerpt}</p>
                  <time className="mono-small text-grey">
                    {format(new Date(post.publishedAt), 'MMMM d, yyyy')}
                  </time>
                </Card>
              </Link>
            </motion.div>
          </Column>
        ))}
      </Grid>
    </div>
    </Layout>
    </>
  )
}
